import {
  CommunityCupConfig,
  CommunityCupDivisionData,
  CourtAvailability,
  DivisionState,
  EventState,
  GroupStageState,
  Match,
  PlayerProfile,
  ScheduleConfig,
  ScheduleEntry,
  ScheduleCourt,
  TeamProfile,
  TournamentMatchResult,
  TournamentMatchTeam,
  TournamentPlayerMapping,
  TournamentResultsImport,
  TournamentImportStatus,
  TournamentScoreSet,
  TournamentResultType,
} from "@/types/tournament";
import { normalizeRoundRobinState } from "@/utils/roundRobinUtils";
import { createDivisionSlug } from "@/utils/divisionUtils";
import { filterScheduleForDivision } from "@/utils/scheduleUtils";
import { ensureTeamProfiles, sanitizePlayerGender } from "@/utils/teamProfileUtils";
import { createJokerWindows } from "@/utils/communityCup/scoring";
import { normalizeRosterPreparation, materializeCommunityCupPairs } from "@/lib/community-cup/rosterPreparation";

export type { DivisionState };

export const MAX_DIVISIONS = 12;
export const DEFAULT_NUM_PAIRS = 8;
export const EVENT_STORAGE_KEY = "ccb-event-state-v1";

export const createEmptyDivision = (
  id: string,
  name: string,
  numPairs: number = DEFAULT_NUM_PAIRS
): DivisionState => {
  const pairs = Array.from({ length: numPairs }, (_, index) => `Pair ${index + 1}`);
  return {
    id,
    name,
    pairs,
    teamProfiles: ensureTeamProfiles(id, pairs, []),
    mainBracket: {},
    consolationBracket: {},
    placementMatches: {},
    participantMode: "teams",
    participantCount: numPairs,
    numPairs,
    fullPlacement: true,
    showPlacementMatches: true,
    tournamentGenerated: false,
    seedAssignments: {},
    spreadTopSeeds: false,
    isActive: true,
    carouselEnabled: true,
    uploadedSchedule: [],
    randomizeDraw: false,
    groupStage: null,
    roundRobin: null,
  };
};

const ensurePairList = (pairs: unknown, desiredLength: number) => {
  const names = Array.isArray(pairs) ? pairs : [];
  const sanitized = names.map((value) => (typeof value === "string" ? value : ""));
  const result: string[] = [];
  for (let index = 0; index < desiredLength; index++) {
    const existing = sanitized[index]?.trim();
    result.push(existing ? existing : `Pair ${index + 1}`);
  }
  return result;
};

const normalizeGroupStage = (value: unknown): GroupStageState | null => {
  if (!value || typeof value !== "object") return null;
  const raw = value as Partial<GroupStageState> & { groups?: unknown; matches?: unknown };
  const totalTeams: 4 | 8 = raw?.totalTeams === 8 ? 8 : 4;
  const groupCount = totalTeams === 8 ? 2 : 1;
  const sourceGroups = Array.isArray(raw.groups) ? raw.groups : [];

  const groups = Array.from({ length: groupCount }, (_, index) => {
    const source = sourceGroups[index] as Partial<GroupStageState["groups"][number]> | undefined;
    const teamsSource = Array.isArray(source?.teams) ? source?.teams : [];
    const teams: (string | null)[] = [];
    for (let slot = 0; slot < 4; slot++) {
      const entry = teamsSource[slot];
      teams.push(typeof entry === "string" ? entry : null);
    }
    return {
      id:
        typeof source?.id === "string" && source.id.trim().length > 0
          ? source.id.trim()
          : `group-${index + 1}`,
      label:
        typeof source?.label === "string" && source.label.trim().length > 0
          ? source.label
          : `Group ${String.fromCharCode(65 + index)}`,
      teams,
    };
  });

  const matches = Array.isArray(raw.matches)
    ? (raw.matches as unknown[])
        .filter((match): match is Partial<GroupStageState["matches"][number]> => typeof match === "object" && match !== null)
        .map((match, index) => {
          const status =
            match?.status === "currently-playing" || match?.status === "finished" || match?.status === "up-next"
              ? match.status
              : "pending";
          const groupId =
            typeof match?.groupId === "string" && match.groupId.trim().length > 0
              ? match.groupId
              : groups[0]?.id ?? "group-1";
          const mapped: GroupStageState["matches"][number] = {
            id: typeof match?.id === "string" && match.id.trim().length > 0 ? match.id : `group-match-${index + 1}`,
            groupId,
            roundLabel:
              typeof match?.roundLabel === "string" && match.roundLabel.trim().length > 0
                ? match.roundLabel
                : "Round robin",
            pair1: typeof match?.pair1 === "string" && match.pair1.trim().length > 0 ? match.pair1 : "TBD",
            pair2: typeof match?.pair2 === "string" && match.pair2.trim().length > 0 ? match.pair2 : "TBD",
            teamOneSlot:
              typeof match?.teamOneSlot === "string" && match.teamOneSlot.trim().length > 0
                ? match.teamOneSlot
                : `${groupId}-slot-1`,
            teamTwoSlot:
              typeof match?.teamTwoSlot === "string" && match.teamTwoSlot.trim().length > 0
                ? match.teamTwoSlot
                : `${groupId}-slot-2`,
            score1: typeof match?.score1 === "string" ? match.score1 : "",
            score2: typeof match?.score2 === "string" ? match.score2 : "",
            winner: typeof match?.winner === "string" ? match.winner : null,
            status,
            statusSource: match?.statusSource === "manual" ? "manual" : "auto",
            scheduledTime: typeof match?.scheduledTime === "string" ? match.scheduledTime : undefined,
            scheduledCourt: typeof match?.scheduledCourt === "string" ? match.scheduledCourt : undefined,
          };
          return mapped;
        })
    : [];

  return {
    totalTeams,
    groups,
    matches,
    thirdPlaceEnabled: Boolean(raw.thirdPlaceEnabled),
    consolationEnabled: Boolean(raw.consolationEnabled),
    crossoverFinalsOnly: Boolean((raw as GroupStageState).crossoverFinalsOnly),
  };
};

const DEFAULT_SLOT_MINUTES = 30;

const buildDefaultAvailability = (): CourtAvailability => ({
  id: "availability-1",
  label: "Block 1",
  startTime: "09:00",
  endTime: "18:00",
});

const buildDefaultAvailabilityList = (): CourtAvailability[] => [buildDefaultAvailability()];

const buildBaseScheduleConfig = (): ScheduleConfig => ({
  slotMinutes: DEFAULT_SLOT_MINUTES,
  courts: [
    {
      id: "court-1",
      name: "Court 1",
      availability: buildDefaultAvailabilityList(),
    },
  ],
});

const toTrimmedString = (value: unknown): string => (typeof value === "string" ? value.trim() : "");

const toLabeledString = (value: unknown, fallback: string) => {
  const trimmed = toTrimmedString(value);
  return trimmed.length > 0 ? trimmed : fallback;
};

const toTimeString = (value: unknown, fallback: string) => {
  const trimmed = toTrimmedString(value);
  return trimmed.length > 0 ? trimmed : fallback;
};

const toIdString = (value: unknown, fallback: string) => {
  if (typeof value === "string" && value.trim().length > 0) return value.trim();
  if (typeof value === "number") return String(value);
  return fallback;
};

const parseAvailabilityBlocks = (
  blocks: unknown,
  fallbackLabelPrefix: string,
  defaultBlocks: CourtAvailability[]
): CourtAvailability[] => {
  if (!Array.isArray(blocks) || blocks.length === 0) {
    return defaultBlocks;
  }

  const parsed = blocks
    .filter((block): block is Record<string, unknown> => typeof block === "object" && block !== null)
    .map((block, index) => {
      const id = toIdString(block.id, `availability-${index + 1}`);
      const label = toLabeledString(block.label, `${fallbackLabelPrefix} ${index + 1}`);
      const startTime = toTimeString(block.startTime, "09:00");
      const endTime = toTimeString(block.endTime, "18:00");
      return { id, label, startTime, endTime };
    })
    .filter((block) => block.label.length > 0);

  return parsed.length > 0 ? parsed : defaultBlocks;
};

const parseCourts = (
  courts: unknown,
  legacyAvailability: CourtAvailability[],
  defaultAvailability: CourtAvailability[]
): ScheduleCourt[] => {
  if (!Array.isArray(courts) || courts.length === 0) {
    return [
      {
        id: "court-1",
        name: "Court 1",
        availability: legacyAvailability.length > 0 ? legacyAvailability : buildDefaultAvailabilityList(),
      },
    ];
  }

  const parsed = courts
    .filter((court): court is Record<string, unknown> => typeof court === "object" && court !== null)
    .map((court, index) => {
      const nameFallback = `Court ${index + 1}`;
      const name = toLabeledString(court.name, nameFallback);
      const availabilitySource = parseAvailabilityBlocks(
        court.availability,
        name || nameFallback,
        legacyAvailability
      );
      return {
        id: toIdString(court.id, `court-${index + 1}`),
        name,
        availability:
          availabilitySource.length > 0
            ? availabilitySource
            : legacyAvailability.length > 0
              ? legacyAvailability
              : defaultAvailability,
      };
    })
    .filter((court) => court.name.trim().length > 0);

  if (parsed.length === 0) {
    return [
      {
        id: "court-1",
        name: "Court 1",
        availability: legacyAvailability.length > 0 ? legacyAvailability : buildDefaultAvailabilityList(),
      },
    ];
  }

  return parsed;
};

export const normalizeScheduleConfig = (config: unknown): ScheduleConfig => {
  if (!config || typeof config !== "object") {
    return buildBaseScheduleConfig();
  }

  const raw = config as { slotMinutes?: unknown; courts?: unknown; days?: unknown };
  const slotMinutes =
    typeof raw.slotMinutes === "number" && Number.isFinite(raw.slotMinutes) && raw.slotMinutes > 0
      ? Math.floor(raw.slotMinutes)
      : DEFAULT_SLOT_MINUTES;

  const legacyAvailability = parseAvailabilityBlocks(raw.days, "Day", buildDefaultAvailabilityList());
  const courts = parseCourts(raw.courts, legacyAvailability, buildDefaultAvailabilityList());

  return {
    slotMinutes,
    courts,
  };
};

export const createBaselineEventState = (): EventState => {
  const initialDivisionId = "division-1";
  const initialDivision = createEmptyDivision(initialDivisionId, "Division 1");

  return {
    eventName: "Padel Tournament",
    venue: "",
    venueId: "",
    preventDisplaySleep: true,
    divisions: { [initialDivisionId]: initialDivision },
    divisionOrder: [initialDivisionId],
    activeDivisionId: initialDivisionId,
    carouselDivisionIds: [initialDivisionId],
    uploadedSchedule: [],
    carouselIntervalSeconds: 30,
    sponsorLogoDataUrl: null,
    scheduleConfig: buildBaseScheduleConfig(),
    resultsImports: [],
  };
};

export const cloneBracketMatches = (bracket: Record<string, Match[]>) =>
  Object.entries(bracket).reduce<Record<string, Match[]>>((acc, [roundId, matches]) => {
    acc[roundId] = matches.map((match) => ({ ...match }));
    return acc;
  }, {});

export const clonePlacementMatches = (placement: Record<string, Match>) =>
  Object.entries(placement).reduce<Record<string, Match>>((acc, [key, match]) => {
    acc[key] = { ...match };
    return acc;
  }, {});

export const cloneGroupStage = (groupStage: GroupStageState | null | undefined): GroupStageState | null => {
  if (!groupStage) return null;
  return {
    ...groupStage,
    crossoverFinalsOnly: Boolean(groupStage.crossoverFinalsOnly),
    groups: groupStage.groups.map((group, index) => ({
      id: group.id || `group-${index + 1}`,
      label: group.label || `Group ${String.fromCharCode(65 + index)}`,
      teams: Array.isArray(group.teams)
        ? group.teams.map((team) => (typeof team === "string" ? team : null))
        : Array.from({ length: 4 }, () => null),
    })),
    matches: groupStage.matches.map((match, matchIndex) => ({
      ...match,
      id: match.id || `group-match-${matchIndex + 1}`,
    })),
  };
};

export const cloneScheduleEntries = (schedule: ScheduleEntry[]) => schedule.map((entry) => ({ ...entry }));

export const cloneTeamProfiles = (profiles: TeamProfile[]) =>
  profiles.map((profile) => ({
    ...profile,
    players: profile.players.map((player) => ({ ...player })),
  }));

export const appendOriginalSuffix = (name: string) => {
  const trimmed = name.trim();
  return trimmed.toLowerCase().endsWith(" original") ? trimmed : `${trimmed} original`;
};

export const cloneDivisionState = (division: DivisionState): DivisionState => ({
  ...division,
  pairs: [...division.pairs],
  teamProfiles: cloneTeamProfiles(division.teamProfiles),
  mainBracket: cloneBracketMatches(division.mainBracket),
  consolationBracket: cloneBracketMatches(division.consolationBracket),
  placementMatches: clonePlacementMatches(division.placementMatches),
  uploadedSchedule: cloneScheduleEntries(division.uploadedSchedule),
  groupStage: cloneGroupStage(division.groupStage),
  roundRobin: division.roundRobin
    ? {
        ...division.roundRobin,
        teams: [...division.roundRobin.teams],
        matches: division.roundRobin.matches.map((m) => ({ ...m })),
        byeTeamPerRound: [...division.roundRobin.byeTeamPerRound],
      }
    : null,
});

export const cloneEventState = (event: EventState): EventState => {
  const divisions: Record<string, DivisionState> = {};
  Object.entries(event.divisions).forEach(([divisionId, division]) => {
    divisions[divisionId] = cloneDivisionState(division);
  });

  const clonePlayerProfile = (profile: PlayerProfile): PlayerProfile => ({
    ...profile,
  });

  const clonePlayerMapping = (mapping: TournamentPlayerMapping): TournamentPlayerMapping => ({
    ...mapping,
    player: mapping.player ? clonePlayerProfile(mapping.player) : null,
  });

  const cloneMatchResult = (match: TournamentMatchResult): TournamentMatchResult => ({
    ...match,
    team1Players: [...match.team1Players],
    team2Players: [...match.team2Players],
    scoreSets: Array.isArray(match.scoreSets)
      ? match.scoreSets.map((set) => ({ ...set }))
      : undefined,
    team1: match.team1
      ? {
          label: match.team1.label,
          playerNames: [...match.team1.playerNames],
          playerIds: [...match.team1.playerIds],
          teamId: match.team1.teamId,
        }
      : {
          label: match.team1Label,
          playerNames: [...match.team1Players],
          playerIds: [],
          teamId: null,
        },
    team2: match.team2
      ? {
          label: match.team2.label,
          playerNames: [...match.team2.playerNames],
          playerIds: [...match.team2.playerIds],
          teamId: match.team2.teamId,
        }
      : {
          label: match.team2Label,
          playerNames: [...match.team2Players],
          playerIds: [],
          teamId: null,
        },
  });

  const cloneResultsImports = (imports: TournamentResultsImport[] = []): TournamentResultsImport[] =>
    imports.map((entry) => ({
      ...entry,
      matches: entry.matches.map(cloneMatchResult),
      playerMappings: entry.playerMappings.map(clonePlayerMapping),
    }));

  return {
    ...event,
    divisions,
    divisionOrder: [...event.divisionOrder],
    activeDivisionId: event.activeDivisionId,
    carouselDivisionIds: [...event.carouselDivisionIds],
    uploadedSchedule: cloneScheduleEntries(event.uploadedSchedule),
    scheduleConfig: {
      slotMinutes: event.scheduleConfig.slotMinutes,
      courts: event.scheduleConfig.courts.map((court, index) => ({
        id: court.id || `court-${index + 1}`,
        name: court.name,
        availability:
          Array.isArray(court.availability) && court.availability.length > 0
            ? court.availability.map((block, blockIndex) => ({
                id: block.id || `availability-${blockIndex + 1}`,
                label: block.label,
                startTime: block.startTime,
                endTime: block.endTime,
              }))
            : [
                {
                  id: `availability-default-${index + 1}`,
                  label: "Block 1",
                  startTime: "09:00",
                  endTime: "18:00",
                },
              ],
      })),
    },
    resultsImports: cloneResultsImports(event.resultsImports),
  };
};

const normalizeDivisionState = (
  division: Partial<DivisionState> | undefined,
  fallbackId: string,
  fallbackName: string,
  includeInCarousel: boolean
): DivisionState => {
  const initialId = division?.id ?? fallbackId;
  const initialName = division?.name ?? fallbackName;
  const initialPairs = division?.numPairs ?? DEFAULT_NUM_PAIRS;
  const baseline = createEmptyDivision(initialId, initialName, initialPairs);

  const participantMode: "teams" | "players" =
    division?.participantMode === "players" ? "players" : "teams";

  const rawParticipantCount =
    typeof division?.participantCount === "number" && Number.isFinite(division.participantCount)
      ? Math.max(0, Math.floor(division.participantCount))
      : undefined;

  let participantCount =
    rawParticipantCount ??
    (participantMode === "teams" ? division?.numPairs ?? baseline.numPairs : (division?.numPairs ?? baseline.numPairs) * 2);

  if (participantMode === "teams") {
    participantCount = Math.max(2, participantCount);
  } else {
    participantCount = Math.max(4, Math.ceil(participantCount / 4) * 4);
  }

  const numPairs =
    participantMode === "players"
      ? Math.max(1, Math.ceil(participantCount / 2))
      : Math.max(2, division?.numPairs ?? baseline.numPairs);

  const pairs = ensurePairList(division?.pairs, numPairs);

  const seedAssignmentsSource =
    division?.seedAssignments && typeof division.seedAssignments === "object"
      ? division.seedAssignments
      : {};
  const filteredSeeds: Record<string, number> = {};
  pairs.forEach((pair) => {
    const seedValue = (seedAssignmentsSource as Record<string, unknown>)[pair];
    if (typeof seedValue === "number") {
      filteredSeeds[pair] = seedValue;
    }
  });

  const teamProfilesSource = Array.isArray(division?.teamProfiles)
    ? division.teamProfiles
    : undefined;
  const teamProfiles = ensureTeamProfiles(baseline.id, pairs, teamProfilesSource);

  return {
    ...baseline,
    ...division,
    id: division?.id ?? fallbackId,
    name: division?.name ?? fallbackName,
    participantMode,
    participantCount,
    numPairs,
    pairs,
    teamProfiles,
    seedAssignments: filteredSeeds,
    uploadedSchedule: Array.isArray(division?.uploadedSchedule)
      ? division.uploadedSchedule
      : [],
    mainBracket: division?.mainBracket ?? baseline.mainBracket,
    consolationBracket: division?.consolationBracket ?? baseline.consolationBracket,
    placementMatches: division?.placementMatches ?? baseline.placementMatches,
    fullPlacement: division?.fullPlacement ?? baseline.fullPlacement,
    showPlacementMatches:
      division?.showPlacementMatches ?? baseline.showPlacementMatches,
    tournamentGenerated: division?.tournamentGenerated ?? baseline.tournamentGenerated,
    spreadTopSeeds: division?.spreadTopSeeds ?? baseline.spreadTopSeeds,
    isActive: division?.isActive ?? baseline.isActive,
    carouselEnabled: includeInCarousel,
    randomizeDraw: division?.randomizeDraw ?? baseline.randomizeDraw,
    groupStage: normalizeGroupStage(division?.groupStage) ?? baseline.groupStage,
    roundRobin: normalizeRoundRobinState(division?.roundRobin) ?? baseline.roundRobin,
  };
};

const toNullableString = (value: unknown): string | null => {
  if (typeof value === "string") {
    const trimmed = value.trim();
    return trimmed.length > 0 ? trimmed : null;
  }
  return null;
};

const parseImportDate = (value: string | null): string | null => {
  if (!value) return null;
  const trimmed = value.trim();
  if (!trimmed) return null;
  const slashParts = trimmed.split("/");
  if (slashParts.length === 3) {
    const [part1, part2, part3] = slashParts.map((part) => part.trim());
    if (part1.length === 4) {
      return `${part1.padStart(4, "0")}-${part2.padStart(2, "0")}-${part3.padStart(2, "0")}`;
    }
    if (part3.length === 4) {
      return `${part3.padStart(4, "0")}-${part2.padStart(2, "0")}-${part1.padStart(2, "0")}`;
    }
  }
  const dashParts = trimmed.split("-");
  if (dashParts.length === 3) {
    const [year, month, day] = dashParts;
    if (year.length === 4) {
      return `${year.padStart(4, "0")}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`;
    }
  }
  const parsed = new Date(trimmed);
  if (!Number.isNaN(parsed.getTime())) {
    return parsed.toISOString().split("T")[0];
  }
  return null;
};

const normalizeResultLabel = (value: string): string =>
  value
    .toLowerCase()
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[-/&+,]/g, " ")
    .replace(/[^a-z0-9\s]/g, "")
    .replace(/\s+/g, " ")
    .trim();

const normalizeTimeValue = (value: string | null): { raw: string | null; normalized: string | null } => {
  if (!value) return { raw: null, normalized: null };
  const raw = value.trim();
  if (!raw) return { raw: null, normalized: null };

  const twelveHour = raw.match(/^(\d{1,2})(?::|\.)(\d{2})(?::(\d{2}))?\s*(AM|PM)$/i);
  if (twelveHour) {
    let hours = Number(twelveHour[1]);
    const minutes = Number(twelveHour[2]);
    if (!Number.isFinite(hours) || !Number.isFinite(minutes)) {
      return { raw, normalized: raw };
    }
    const meridiem = twelveHour[4]?.toUpperCase();
    if (meridiem === "PM" && hours < 12) {
      hours += 12;
    } else if (meridiem === "AM" && hours === 12) {
      hours = 0;
    }
    return {
      raw,
      normalized: `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`,
    };
  }

  const simple = raw.match(/^(\d{1,2})(?::|\.)(\d{2})(?::(\d{2}))?$/);
  if (simple) {
    const hours = Number(simple[1]);
    const minutes = Number(simple[2]);
    if (!Number.isFinite(hours) || !Number.isFinite(minutes)) {
      return { raw, normalized: raw };
    }
    return {
      raw,
      normalized: `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`,
    };
  }

  const numeric = raw.match(/^(\d{1,2})(\d{2})$/);
  if (numeric) {
    const hours = Number(numeric[1]);
    const minutes = Number(numeric[2]);
    if (Number.isFinite(hours) && Number.isFinite(minutes)) {
      return {
        raw,
        normalized: `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`,
      };
    }
  }

  return { raw, normalized: raw };
};

const parseScoreValue = (value: unknown): number | null => {
  if (typeof value === "number" && Number.isFinite(value)) {
    return Math.trunc(value);
  }
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed) return null;
    const parsed = Number(trimmed);
    if (Number.isFinite(parsed)) {
      return Math.trunc(parsed);
    }
  }
  return null;
};

const determineTieBreakType = (team1: number | null, team2: number | null, raw: string): TournamentScoreSet["tieBreakType"] => {
  const normalizedRaw = raw.toLowerCase();
  if (normalizedRaw.includes("super")) return "super";
  if (normalizedRaw.includes("tb") || normalizedRaw.includes("tie-break") || normalizedRaw.includes("tiebreak")) {
    return "standard";
  }
  if (team1 == null || team2 == null) return "none";
  const maxScore = Math.max(team1, team2);
  const minScore = Math.min(team1, team2);
  if (maxScore >= 10) return "super";
  if (Math.abs(team1 - team2) === 1) return "standard";
  if (maxScore === 7 && minScore >= 5) return "standard";
  return "none";
};

const parseScoreSetsFromString = (score: string): TournamentScoreSet[] | undefined => {
  if (!score) return undefined;
  const segments = score
    .split("/")
    .map((segment) => segment.trim())
    .filter((segment) => segment.length > 0);
  if (segments.length === 0) {
    return undefined;
  }
  const hyphenRegex = /-|–|—/;
  const sets = segments.map((segment) => {
    const [first, second] = segment.split(hyphenRegex).map((part) => part.trim());
    return {
      raw: segment,
      team1: parseScoreValue(first),
      team2: parseScoreValue(second),
      tieBreakType: determineTieBreakType(parseScoreValue(first), parseScoreValue(second), segment),
    };
  });
  return sets;
};

const normalizeScoreSets = (
  score: string,
  rawSets: unknown,
): TournamentScoreSet[] | undefined => {
  if (Array.isArray(rawSets)) {
    const parsed = rawSets
      .filter((entry): entry is Partial<TournamentScoreSet> => typeof entry === "object" && entry !== null)
      .map((entry) => {
        const raw = typeof entry.raw === "string" ? entry.raw.trim() : "";
        const team1 = parseScoreValue(entry.team1);
        const team2 = parseScoreValue(entry.team2);
        let tieBreakType: TournamentScoreSet["tieBreakType"] = "none";
        if (typeof (entry as { tieBreakType?: unknown }).tieBreakType === "string") {
          const normalizedType = String((entry as { tieBreakType?: string }).tieBreakType).trim().toLowerCase();
          if (normalizedType === "standard" || normalizedType === "super") {
            tieBreakType = normalizedType;
          } else if (normalizedType === "none") {
            tieBreakType = "none";
          } else {
            tieBreakType = determineTieBreakType(team1, team2, raw);
          }
        } else {
          tieBreakType = determineTieBreakType(team1, team2, raw);
        }
        return raw
          ? {
              raw,
              team1,
              team2,
              tieBreakType,
            }
          : null;
      })
      .filter((entry): entry is TournamentScoreSet => Boolean(entry));
    if (parsed.length > 0) {
      return parsed;
    }
  }
  return parseScoreSetsFromString(score);
};

const detectTieBreak = (sets: TournamentScoreSet[] | undefined, score: string): boolean => {
  if (Array.isArray(sets)) {
    return sets.some((set) => set.tieBreakType !== "none");
  }
  return /tb|tie-break|tiebreak/i.test(score.toLowerCase());
};

const normalizePlayerProfile = (value: unknown): PlayerProfile | null => {
  if (!value || typeof value !== "object") return null;
  const record = value as Partial<PlayerProfile>;
  const id = record.id != null ? record.id.toString().trim() : "";
  if (!id) return null;
  return {
    id,
    name: typeof record.name === "string" ? record.name : "",
    alias: typeof record.alias === "string" && record.alias.trim().length > 0 ? record.alias : (typeof record.name === "string" ? record.name : ""),
    gender: sanitizePlayerGender(record.gender),
    isExpat: Boolean(record.isExpat),
    startingLevel: typeof record.startingLevel === "string" && record.startingLevel.trim().length > 0
      ? record.startingLevel
      : "Unspecified",
  };
};

const normalizeMatchResult = (value: unknown, fallbackId: string): TournamentMatchResult | null => {
  if (!value || typeof value !== "object") return null;
  const record = value as Partial<TournamentMatchResult> & {
    team1Players?: unknown;
    team2Players?: unknown;
  };

  const id = typeof record.id === "string" && record.id.trim().length > 0 ? record.id.trim() : fallbackId;

  const team1Players = Array.isArray(record.team1Players)
    ? record.team1Players.filter((entry): entry is string => typeof entry === "string").map((entry) => entry.trim()).filter(Boolean)
    : [];
  const team2Players = Array.isArray(record.team2Players)
    ? record.team2Players.filter((entry): entry is string => typeof entry === "string").map((entry) => entry.trim()).filter(Boolean)
    : [];

  const team1Label =
    typeof record.team1Label === "string" && record.team1Label.trim().length > 0
      ? record.team1Label.trim()
      : (team1Players.length > 0 ? team1Players.join(" / ") : "Team A");

  const team2Label =
    typeof record.team2Label === "string" && record.team2Label.trim().length > 0
      ? record.team2Label.trim()
      : (team2Players.length > 0 ? team2Players.join(" / ") : "Team B");

  const rawDate =
    toNullableString((record as { rawDate?: unknown }).rawDate) ?? toNullableString(record.date);
  const normalizedDate =
    toNullableString(record.date) ?? (rawDate ? parseImportDate(rawDate) : null);

  const providedRawTime =
    toNullableString((record as { rawTime?: unknown }).rawTime) ?? toNullableString(record.time);
  const timeInfo = normalizeTimeValue(providedRawTime);
  const normalizedTime =
    toNullableString(record.time) ?? (timeInfo.normalized ?? timeInfo.raw);

  const scoreRaw = typeof record.score === "string" ? record.score : "";
  const scoreText = scoreRaw.replace(/_/g, "-");
  const trimmedScore = scoreText.trim();
  const scoreSets = normalizeScoreSets(scoreText, (record as { scoreSets?: unknown }).scoreSets);
  const hasTieBreak =
    typeof (record as { hasTieBreak?: unknown }).hasTieBreak === "boolean"
      ? Boolean((record as { hasTieBreak?: unknown }).hasTieBreak)
      : detectTieBreak(scoreSets, scoreText);

  const normalizeTeam = (
    value: unknown,
    fallbackLabel: string,
    fallbackNames: string[]
  ): TournamentMatchTeam => {
    if (value && typeof value === "object") {
      const team = value as Partial<TournamentMatchTeam> & {
        playerNames?: unknown;
        playerIds?: unknown;
      };
      const label =
        typeof team.label === "string" && team.label.trim().length > 0
          ? team.label.trim()
          : fallbackLabel;
      const playerNames = Array.isArray(team.playerNames)
        ? team.playerNames
            .map((name) => (typeof name === "string" ? name.trim() : ""))
            .filter((name) => name.length > 0)
        : [...fallbackNames];
      const playerIds = Array.isArray(team.playerIds)
        ? team.playerIds
            .map((id) => (id != null ? String(id).trim() : ""))
            .filter((id) => id.length > 0)
        : [];
      const teamId = typeof team.teamId === "string" && team.teamId.trim().length > 0 ? team.teamId.trim() : null;
      return {
        label,
        playerNames: playerNames.length > 0 ? playerNames : [...fallbackNames],
        playerIds,
        teamId,
      };
    }
    return {
      label: fallbackLabel,
      playerNames: [...fallbackNames],
      playerIds: [],
      teamId: null,
    };
  };

  const team1 = normalizeTeam((record as { team1?: unknown }).team1, team1Label, team1Players);
  const team2 = normalizeTeam((record as { team2?: unknown }).team2, team2Label, team2Players);

  const storedResultType = (record as { resultType?: unknown }).resultType;
  let resultType: TournamentResultType | null =
    storedResultType === "score" || storedResultType === "winner-only" || storedResultType === "incomplete"
      ? storedResultType
      : null;
  const storedWinner = (record as { winner?: unknown }).winner;
  let winner: "team1" | "team2" | null =
    storedWinner === "team1" || storedWinner === "team2" ? storedWinner : null;
  let winnerLabel = toNullableString((record as { winnerLabel?: unknown }).winnerLabel);
  let rawWinner =
    toNullableString((record as { rawWinner?: unknown }).rawWinner) ??
    toNullableString((record as { rawWinnerText?: unknown }).rawWinnerText) ??
    toNullableString((record as { winnerText?: unknown }).winnerText);
  if (!rawWinner && winnerLabel) {
    rawWinner = winnerLabel;
  }

  const deriveWinnerFromRaw = (candidate: string) => {
    const normalizedWinner = normalizeResultLabel(candidate);
    if (!normalizedWinner) return null;
    const collapse = (value: string) => value.replace(/\s+/g, "");
    const normalizedTeam1Label = normalizeResultLabel(team1.label);
    const normalizedTeam2Label = normalizeResultLabel(team2.label);
    const normalizedTeam1Players = normalizeResultLabel(team1.playerNames.join(" / "));
    const normalizedTeam2Players = normalizeResultLabel(team2.playerNames.join(" / "));
    const winnerCollapsed = collapse(normalizedWinner);
    const team1Collapsed = collapse(normalizedTeam1Label);
    const team2Collapsed = collapse(normalizedTeam2Label);
    const team1PlayersCollapsed = collapse(normalizedTeam1Players);
    const team2PlayersCollapsed = collapse(normalizedTeam2Players);
    const matchesTeam1 =
      normalizedWinner === normalizedTeam1Label ||
      normalizedWinner === normalizedTeam1Players ||
      winnerCollapsed === team1Collapsed ||
      winnerCollapsed === team1PlayersCollapsed;
    const matchesTeam2 =
      normalizedWinner === normalizedTeam2Label ||
      normalizedWinner === normalizedTeam2Players ||
      winnerCollapsed === team2Collapsed ||
      winnerCollapsed === team2PlayersCollapsed;
    if (matchesTeam1 && !matchesTeam2) {
      return { side: "team1" as const, label: team1.label };
    }
    if (matchesTeam2 && !matchesTeam1) {
      return { side: "team2" as const, label: team2.label };
    }
    return null;
  };

  if (resultType === "score" && trimmedScore.length === 0) {
    resultType = null;
  }

  if (resultType === "winner-only" && (!winner || !winnerLabel)) {
    const derived = rawWinner ? deriveWinnerFromRaw(rawWinner) : null;
    if (derived) {
      winner = derived.side;
      winnerLabel = derived.label;
    } else if (trimmedScore.length > 0) {
      resultType = "score";
      winner = null;
      winnerLabel = null;
    } else if (!rawWinner) {
      resultType = "incomplete";
      winner = null;
      winnerLabel = null;
    }
  }

  if (!resultType) {
    if (trimmedScore.length > 0) {
      resultType = "score";
      winner = null;
      winnerLabel = null;
    } else if (rawWinner) {
      const derived = deriveWinnerFromRaw(rawWinner);
      if (derived) {
        resultType = "winner-only";
        winner = derived.side;
        winnerLabel = derived.label;
      } else {
        resultType = "incomplete";
        winner = null;
        winnerLabel = null;
      }
    } else {
      resultType = "incomplete";
      winner = null;
      winnerLabel = null;
    }
  }

  if (resultType === "score") {
    winner = null;
    winnerLabel = null;
  } else if (resultType === "winner-only" && (!winner || !winnerLabel) && rawWinner) {
    const derived = deriveWinnerFromRaw(rawWinner);
    if (derived) {
      winner = derived.side;
      winnerLabel = derived.label;
    } else if (trimmedScore.length > 0) {
      resultType = "score";
      winner = null;
      winnerLabel = null;
    } else {
      resultType = "incomplete";
      winner = null;
      winnerLabel = null;
    }
  }

  if (!resultType) {
    resultType = trimmedScore.length > 0 ? "score" : "incomplete";
    winner = null;
    winnerLabel = null;
  }

  if (rawWinner) {
    const trimmed = rawWinner.trim();
    rawWinner = trimmed.length > 0 ? trimmed : null;
  }

  return {
    id,
    date: normalizedDate,
    rawDate,
    time: normalizedTime,
    rawTime: timeInfo.raw,
    stage: toNullableString(record.stage),
    group: toNullableString(record.group),
    team1Label,
    team2Label,
    team1Players,
    team2Players,
    score: scoreText,
    resultType: resultType ?? "incomplete",
    winner,
    winnerLabel,
    rawWinner,
    hasTieBreak,
    scoreSets,
    team1,
    team2,
  };
};

const normalizePlayerMapping = (
  value: unknown,
  fallbackRaw: string,
  fallbackId: string
): TournamentPlayerMapping | null => {
  if (!value || typeof value !== "object") {
    return {
      id: fallbackId,
      rawName: fallbackRaw,
      normalizedName: fallbackRaw.toLowerCase(),
      player: null,
    };
  }

  const record = value as Partial<TournamentPlayerMapping>;
  const rawName =
    typeof record.rawName === "string" && record.rawName.trim().length > 0
      ? record.rawName.trim()
      : fallbackRaw;

  return {
    id:
      typeof record.id === "string" && record.id.trim().length > 0
        ? record.id.trim()
        : fallbackId,
    rawName,
    normalizedName:
      typeof record.normalizedName === "string" && record.normalizedName.trim().length > 0
        ? record.normalizedName.trim()
        : rawName.toLowerCase(),
    player: normalizePlayerProfile(record.player),
  };
};

const normalizeImportStatus = (value: unknown): TournamentImportStatus => {
  if (value === "submitted" || value === "uploaded") return value;
  return "draft";
};

const normalizeResultsImports = (value: unknown): TournamentResultsImport[] => {
  if (!Array.isArray(value)) return [];

  return value
    .map((entry, index) => {
      if (!entry || typeof entry !== "object") return null;
      const record = entry as Partial<TournamentResultsImport> & {
        matches?: unknown;
        playerMappings?: unknown;
      };

      const fallbackId = `results-import-${index + 1}`;
      const matches: TournamentMatchResult[] = Array.isArray(record.matches)
        ? record.matches
            .map((match, matchIndex) => normalizeMatchResult(match, `${fallbackId}-match-${matchIndex + 1}`))
            .filter((match): match is TournamentMatchResult => Boolean(match))
        : [];

      const playerMappings: TournamentPlayerMapping[] = Array.isArray(record.playerMappings)
        ? record.playerMappings
            .map((mapping, mappingIndex) => normalizePlayerMapping(mapping, "", `${fallbackId}-player-${mappingIndex + 1}`))
            .filter((mapping): mapping is TournamentPlayerMapping => Boolean(mapping))
        : [];

      return {
        id: typeof record.id === "string" && record.id.trim().length > 0 ? record.id.trim() : fallbackId,
        createdAt: typeof record.createdAt === "string" ? record.createdAt : new Date().toISOString(),
        updatedAt: typeof record.updatedAt === "string" ? record.updatedAt : new Date().toISOString(),
        tournamentName: typeof record.tournamentName === "string" ? record.tournamentName : "",
        venue: typeof record.venue === "string" ? record.venue : "",
        startDate: toNullableString(record.startDate),
        endDate: toNullableString(record.endDate),
        sourceFileName: typeof record.sourceFileName === "string" ? record.sourceFileName : "",
        sourceSheetName: toNullableString(record.sourceSheetName),
        notes: typeof record.notes === "string" ? record.notes : undefined,
        status: normalizeImportStatus(record.status),
        matches,
        playerMappings,
        venueId: (() => {
          if (typeof record.venueId === "string") {
            const trimmed = record.venueId.trim();
            return trimmed.length > 0 ? trimmed : null;
          }
          if (typeof record.venueId === "number") {
            return String(record.venueId);
          }
          return null;
        })(),
        divisionId: typeof record.divisionId === "string" ? record.divisionId : null,
        divisionName:
          typeof record.divisionName === "string" && record.divisionName.trim().length > 0
            ? record.divisionName.trim()
            : null,
        divisionGender: ((): TournamentResultsImport["divisionGender"] => {
          const genderRaw = (record as { divisionGender?: unknown }).divisionGender;
          if (genderRaw === "male" || genderRaw === "female" || genderRaw === "unspecified") {
            return genderRaw;
          }
          if (genderRaw === "mixed") return "mixed";
          return null;
        })(),
        lastUploadedAt: typeof record.lastUploadedAt === "string" ? record.lastUploadedAt : null,
        uploadError:
          typeof record.uploadError === "string"
            ? record.uploadError
            : record.uploadError === null
              ? null
              : undefined,
      };
    })
    .filter((entry): entry is NonNullable<typeof entry> => entry !== null);
};

export const normalizeEventState = (raw: unknown): EventState | null => {
  if (!raw || typeof raw !== "object") return null;
  const candidate = raw as Partial<EventState>;

  if (!candidate.divisionOrder || !candidate.divisions) return null;
  const divisionOrder = Array.isArray(candidate.divisionOrder)
    ? candidate.divisionOrder.filter((value): value is string => typeof value === "string" && value.length > 0)
    : [];
  if (divisionOrder.length === 0) return null;

  const carouselSet = new Set(
    Array.isArray(candidate.carouselDivisionIds)
      ? candidate.carouselDivisionIds.filter((value): value is string => typeof value === "string")
      : []
  );

  const divisions: Record<string, DivisionState> = {};
  divisionOrder.forEach((divisionId, index) => {
    const source = (candidate.divisions as Record<string, Partial<DivisionState> | undefined>)[divisionId];
    const normalized = normalizeDivisionState(
      source,
      divisionId,
      source?.name ?? `Division ${index + 1}`,
      carouselSet.has(divisionId)
    );
    divisions[divisionId] = normalized;
  });

  const activeDivisionId = divisionOrder.includes(candidate.activeDivisionId ?? "")
    ? candidate.activeDivisionId
    : divisionOrder[0];

  const carouselDivisionIds = divisionOrder.filter((divisionId) => carouselSet.has(divisionId));
  if (carouselDivisionIds.length === 0 && activeDivisionId) {
    carouselDivisionIds.push(activeDivisionId);
  }

  return {
    eventName: candidate.eventName ?? "Padel Tournament",
    venue: candidate.venue ?? "",
    venueId: typeof candidate.venueId === "string" ? candidate.venueId : "",
    preventDisplaySleep:
      typeof candidate.preventDisplaySleep === "boolean"
        ? candidate.preventDisplaySleep
        : true,
    divisions,
    divisionOrder,
    activeDivisionId: activeDivisionId ?? null,
    carouselDivisionIds: carouselDivisionIds.length > 0 ? carouselDivisionIds : divisionOrder.slice(0, 1),
    uploadedSchedule: Array.isArray(candidate.uploadedSchedule) ? candidate.uploadedSchedule : [],
    carouselIntervalSeconds:
      typeof candidate.carouselIntervalSeconds === "number"
        ? candidate.carouselIntervalSeconds
        : 30,
    sponsorLogoDataUrl:
      typeof candidate.sponsorLogoDataUrl === "string"
        ? candidate.sponsorLogoDataUrl
        : candidate.sponsorLogoDataUrl === null
          ? null
          : null,
    scheduleConfig: normalizeScheduleConfig(candidate.scheduleConfig),
    resultsImports: normalizeResultsImports((candidate as { resultsImports?: unknown }).resultsImports),
  };
};

// Fields that used to live directly on `EventState.communityCup` (a
// `CommunityCupConfig`) before divisions could each carry their own Community
// Cup data. Now they live on `DivisionState.communityCup` (a
// `CommunityCupDivisionData`) instead.
const COMMUNITY_CUP_DIVISION_KEYS = [
  "pairsA",
  "pairsB",
  "fixtures",
  "jokerWindows",
  "roundTimes",
  "savedSchedules",
  "substitutions",
] as const;

// Shape of `EventState.communityCup` as it could still appear in persisted
// (localStorage/cloud) data from before the multi-division migration.
type LegacyCommunityCupConfig = CommunityCupConfig & Partial<CommunityCupDivisionData>;

/**
 * Migrates a legacy, pre-multi-division `EventState.communityCup` shape —
 * where `pairsA` / `pairsB` / `fixtures` / `jokerWindows` / `roundTimes` /
 * `savedSchedules` / `substitutions` lived directly on the event-level
 * `CommunityCupConfig` — into the event's single existing division's
 * `communityCup` field (`DivisionState.communityCup`, a
 * `CommunityCupDivisionData`), and strips those keys from the event-level
 * object so it matches the current `CommunityCupConfig` shape
 * (`communityASide` / `communityBSide` / `scoresPublic` only).
 *
 * Events already using flexible roster preparation are normalized here.
 * Historical division-scoped cups without the explicit version marker stay
 * structurally unchanged; their pages bootstrap a working preparation view
 * lazily without mutating hydrated event state.
 *
 * Wired in from `sanitizeEvent()` in `src/context/OrganizerEventsContext.tsx`,
 * which runs for every event loaded from `ORGANIZER_EVENTS_STORAGE_KEY` and
 * for every cloud event merged in via `mergeCloudEvents` /
 * `buildCloudOnlySnapshot` — so this normalizer runs before any page reads
 * `communityCup` off a hydrated event or its divisions.
 */
export const normalizeCommunityCupDivisionData = (event: EventState): EventState => {
  const rawCommunityCup = event.communityCup as LegacyCommunityCupConfig | undefined;
  if (!rawCommunityCup) return event;

  const withNormalizedPreparation = (source: EventState): EventState => {
    let changed = false;
    const divisions = Object.fromEntries(Object.entries(source.divisions).map(([id, division]) => {
      const communityCup = division.communityCup;
      if (!communityCup) return [id, division];
      if (communityCup.rosterPreparation == null && communityCup.rosterPreparationVersion !== 1) {
        return [id, division];
      }
      const normalizedPreparation = normalizeRosterPreparation(
        communityCup.rosterPreparation,
        communityCup.pairsA,
        communityCup.pairsB,
      );

      // Flexible-roster divisions (rosterPreparationVersion === 1) treat
      // rosterPreparation as the source of truth — pairsA/pairsB are just a
      // materialized snapshot taken at save time. Re-deriving them here on
      // every load, rather than trusting whatever was last persisted, means
      // a division saved while a bug in materializeSidePairs dropped a pair
      // (e.g. the unmatched-but-named-player bug) self-heals the next time
      // anyone loads the event, instead of staying broken until someone
      // happens to resave its roster. Historical divisions
      // (rosterPreparationVersion !== 1) have no rosterPreparation worth
      // deriving from and must keep trusting their persisted pairsA/pairsB
      // exactly as getCommunityCupDivisionReadiness already does.
      const recomputedPairs = communityCup.rosterPreparationVersion === 1
        ? materializeCommunityCupPairs(normalizedPreparation)
        : null;

      const preparationChanged =
        JSON.stringify(communityCup.rosterPreparation) !== JSON.stringify(normalizedPreparation);
      const pairsChanged = recomputedPairs != null && (
        JSON.stringify(communityCup.pairsA) !== JSON.stringify(recomputedPairs.pairsA) ||
        JSON.stringify(communityCup.pairsB) !== JSON.stringify(recomputedPairs.pairsB)
      );
      if (!preparationChanged && !pairsChanged) return [id, division];
      changed = true;
      return [id, {
        ...division,
        communityCup: {
          ...communityCup,
          rosterPreparation: normalizedPreparation,
          ...(recomputedPairs ? { pairsA: recomputedPairs.pairsA, pairsB: recomputedPairs.pairsB } : {}),
        },
      }];
    })) as EventState["divisions"];
    return changed ? { ...source, divisions } : source;
  };

  const hasLegacyFields = COMMUNITY_CUP_DIVISION_KEYS.some(
    (key) => rawCommunityCup[key] !== undefined
  );
  if (!hasLegacyFields) return withNormalizedPreparation(event);

  const divisionId = event.divisionOrder[0];
  const targetDivision = divisionId ? event.divisions[divisionId] : undefined;
  if (!divisionId || !targetDivision) return withNormalizedPreparation(event);

  const {
    pairsA,
    pairsB,
    fixtures,
    jokerWindows,
    roundTimes,
    savedSchedules,
    substitutions,
    ...eventLevelCommunityCup
  } = rawCommunityCup;

  const migratedDivisionData: CommunityCupDivisionData = {
    pairsA: pairsA ?? [],
    pairsB: pairsB ?? [],
    fixtures: fixtures ?? [],
    jokerWindows: jokerWindows ?? createJokerWindows(),
    ...(roundTimes !== undefined ? { roundTimes } : {}),
    ...(savedSchedules !== undefined ? { savedSchedules } : {}),
    ...(substitutions !== undefined ? { substitutions } : {}),
  };

  // The legacy top-level fields are a *fallback* for a division that hasn't
  // been migrated yet — not an override for one that has. If the division
  // already carries its own value for a given field (e.g. a captain's
  // `promoteSignupToPair` RPC already wrote a fresh `pairsA` into the
  // division while the event's top-level `communityCup` still has stale
  // legacy data from before an organizer last re-saved), that value must
  // win. Only fields the division has never set fall back to the migrated
  // legacy value.
  //
  // "Never set" is deliberately NOT the same as "!== undefined" for
  // pairsA/pairsB/fixtures/jokerWindows. `captain_update_division_communitycup`
  // (supabase/migrations/20260728070001_captain_scoped_communitycup_rpc.sql,
  // fixed by 20260809170001_seed_communitycup_bootstrap_from_legacy.sql) can
  // — for an event whose bootstrap predates that fix, or any future writer
  // with the same shape of bug — leave a division holding these fields at
  // their untouched fresh-division defaults (empty arrays / every
  // jokerWindows round "not_open") for every field EXCEPT the one side the
  // caller actually patched. Those untouched defaults carry no information
  // over a still-present, populated legacy top-level value, so treating them
  // as "authoritative" merely because they are `!== undefined` is exactly
  // the data-loss bug: it lets a bootstrap artifact shadow and permanently
  // discard real legacy pairsB/fixtures/jokerWindows. Only a field that has
  // actually diverged from that fresh-division default counts as the
  // division's own authoritative value; roundTimes/savedSchedules/
  // substitutions stay on the simpler `!== undefined` rule because no writer
  // ever bootstraps them to a non-undefined empty placeholder.
  const isUnsetPairsOrFixtures = (value: unknown): boolean =>
    Array.isArray(value) && value.length === 0;

  const isUnsetJokerWindows = (value: unknown): boolean =>
    typeof value === "object" &&
    value !== null &&
    Object.values(value as Record<string, { status?: string }>).every(
      (window) => window?.status === "not_open"
    );

  const existingDivisionFields = targetDivision.communityCup
    ? (Object.fromEntries(
        Object.entries(targetDivision.communityCup).filter(([key, value]) => {
          if (value === undefined) return false;
          if ((key === "pairsA" || key === "pairsB" || key === "fixtures") && isUnsetPairsOrFixtures(value)) {
            return false;
          }
          if (key === "jokerWindows" && isUnsetJokerWindows(value)) {
            return false;
          }
          return true;
        })
      ) as Partial<CommunityCupDivisionData>)
    : {};

  return withNormalizedPreparation({
    ...event,
    communityCup: eventLevelCommunityCup as CommunityCupConfig,
    divisions: {
      ...event.divisions,
      [divisionId]: {
        ...targetDivision,
        communityCup: {
          ...migratedDivisionData,
          ...existingDivisionFields,
        },
      },
    },
  });
};

export const ensureDivisionSlug = (baseName: string, existingIds: Set<string>) => {
  const baseSlug = createDivisionSlug(baseName) || "division";
  let candidate = baseSlug;
  let suffix = 1;
  while (existingIds.has(candidate)) {
    candidate = `${baseSlug}-${suffix++}`;
  }
  return candidate;
};
